// Shared regen utilities — stage lists, mappings, and helpers.
//
// Extracted from regen-view.tsx, regen-dialog.tsx, refine-view.tsx, and
// outline-view.tsx to eliminate 4-way duplication.

import type { PartialRegenStage } from '../storage/idb.js';
import { getPdf } from '../storage/idb.js';
import type { RegenerateStageParams } from '../api/ingest.js';
import { extract } from '@examforge/parser';
import type { CourseDraft, RegenAction } from '@examforge/shared/schemas';
import type { DictionaryKey } from '@examforge/shared/i18n';

// ── Stage definitions ──────────────────────────────────────────────────────────

export interface StageOption {
  id: PartialRegenStage;
  label: string;
}

/** All stages available for partial regeneration. */
export const PARTIAL_REGENERATION_STAGES: StageOption[] = [
  { id: 'mcqs', label: 'MCQ' },
  { id: 'true_false', label: 'True/False' },
  { id: 'flashcards', label: 'Flashcards' },
  { id: 'essays', label: 'Essays' },
  { id: 'glossary', label: 'Glossary' },
  { id: 'mindmaps', label: 'Mindmaps' },
  { id: 'lecture_notes', label: 'Lecture Notes' },
  { id: 'proof_drills', label: 'Proof Drills' },
  { id: 'free_responses', label: 'Free Response' },
  { id: 'reading_passages', label: 'Reading Passages' },
];

/** Maps a PartialRegenStage to the corresponding ComponentId in enabledComponents. */
export const STAGE_TO_COMPONENT_MAP: Record<PartialRegenStage, string> = {
  mcqs: 'quiz_mcq',
  true_false: 'quiz_tf',
  flashcards: 'flashcards',
  essays: 'essay',
  glossary: 'glossary',
  mindmaps: 'mindmap',
  lecture_notes: 'lecture_notes',
  proof_drills: 'proof_drill',
  free_responses: 'free_response',
  reading_passages: 'reading_passage',
};

const STAGE_TO_PACKAGE_FIELD: Record<PartialRegenStage, string> = {
  mcqs: 'mcqs',
  true_false: 'trueFalse',
  flashcards: 'flashcards',
  essays: 'essays',
  glossary: 'glossary',
  mindmaps: 'mindmaps',
  lecture_notes: 'lectureNotes',
  proof_drills: 'proofDrills',
  free_responses: 'freeResponses',
  reading_passages: 'readingPassages',
};

/** Maps a stage ID to its human-readable i18n key (ingest.stage.*). */
const STAGE_LABEL_KEY_MAP: Record<string, DictionaryKey> = {
  parse_files: 'ingest.stage.parseFiles',
  bundle_past_papers: 'ingest.stage.bundlePastPapers',
  extract_cases: 'ingest.stage.extractCases',
  flashcards: 'ingest.stage.flashcards',
  mcqs: 'ingest.stage.mcqs',
  true_false: 'ingest.stage.trueFalse',
  essays: 'ingest.stage.essays',
  mindmaps: 'ingest.stage.mindmaps',
  glossary: 'ingest.stage.glossary',
  lecture_notes: 'ingest.stage.lectureNotes',
  proof_drills: 'ingest.stage.proofDrills',
  free_responses: 'ingest.stage.freeResponses',
  reading_passages: 'ingest.stage.readingPassages',
  mock_papers: 'ingest.stage.mockPapers',
  audit: 'ingest.stage.audit',
};

/**
 * Get the i18n key for a stage's human-readable display name.
 * Returns the matching ingest.stage.* key, falling back to a generic label.
 */
export function stageLabelKey(stageId: string): DictionaryKey {
  return STAGE_LABEL_KEY_MAP[stageId] ?? 'ingest.stage.mcqs';
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Filter stages to only those whose corresponding component is enabled. */
export function getEnabledStages(enabledComponents: readonly string[]): StageOption[] {
  const enabled = new Set(enabledComponents);
  return PARTIAL_REGENERATION_STAGES.filter((s) => {
    const cid = STAGE_TO_COMPONENT_MAP[s.id];
    return cid ? enabled.has(cid) : false;
  });
}

/**
 * Re-parse source files from IDB blobs into markdown slot payloads.
 * Used as fallback for legacy regen endpoint (v4 reads from stored D1 data).
 */
export async function parseSourceSlots(
  sources: CourseDraft['sources'],
): Promise<NonNullable<RegenerateStageParams['slots']>> {
  const parsedSlots: NonNullable<RegenerateStageParams['slots']> = [];
  if (!sources) return parsedSlots;
  for (const slot of sources) {
    const blob = await getPdf(slot.sha256);
    let markdown = '';
    if (blob && (slot.format === 'pdf' || slot.format === 'pptx' || slot.format === 'docx')) {
      try {
        const file = new File([blob], slot.filename, {
          type:
            slot.format === 'pdf'
              ? 'application/pdf'
              : slot.format === 'pptx'
                ? 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
                : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const result = await extract(file);
        markdown = result.markdown;
      } catch {
        /* parse failure → empty markdown; backend will flag */
      }
    } else if (blob && (slot.format === 'md' || slot.format === 'txt')) {
      markdown = await blob.text();
    }
    parsedSlots.push({
      id: slot.id,
      kind: slot.kind,
      format: slot.format,
      filename: slot.filename,
      markdown,
      byteLength: slot.byteLength,
    });
  }
  return parsedSlots;
}

/**
 * Count existing items in the ingest package for a given stage and optional chapter.
 * Used for displaying before/after counts in regen confirm UI.
 */
export function countExistingItems(
  pkg: Record<string, unknown> | undefined | null,
  stageId: PartialRegenStage,
  chapterId: string | undefined,
): number {
  if (!pkg) return 0;
  const fieldName = STAGE_TO_PACKAGE_FIELD[stageId];
  const items = pkg[fieldName] as Array<{ caseId?: string; caseIds?: string[] }> | undefined;
  if (!Array.isArray(items)) return 0;
  if (!chapterId) return items.length;
  return items.filter(
    (it) => it.caseId === chapterId || (it.caseIds ?? []).includes(chapterId),
  ).length;
}

// ── Quick Regen routing ───────────────────────────────────────────────────────

/** Thresholds for Quick (sync) vs Deep (async) regen routing.
 *  Quick Regen runs in the Worker handler synchronously (~30s); Deep Regen
 *  spawns a Cloudflare Workflow and polls for completion.
 *
 *  Design doc: docs/architecture/regen-embedded-design.md §1.4
 *  Audit ref: docs/architecture/audit-regen-2026-06-11.md N1 (calibrate post-launch)
 *
 *  Uses bracket notation for scope access — RegenAction is a discriminated
 *  union and TS doesn't narrow scope fields through dot access. */
export function shouldQuickRegen(action: RegenAction): boolean {
  if (action.kind === 'delete') return true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const scope = action.scope as Record<string, any>;
  if (Array.isArray(scope['itemIds']) && (scope['itemIds'] as unknown[]).length <= 5) return true;
  if (Array.isArray(scope['conceptIds']) && (scope['conceptIds'] as unknown[]).length <= 3) return true;
  if (Array.isArray(scope['caseIds']) && (scope['caseIds'] as unknown[]).length === 1) return true;
  return false;
}

// ── RegenPreview helpers ──────────────────────────────────────────────────────
//
// Extracted from regen-confirm-panel.tsx so both RegenConfirmPanel (legacy)
// and RegenPreview (new) can share the same per-item preview logic.

export interface ItemPreview {
  id: string;
  kindLabel: string;
  /** First line — shown in collapsed state (≤120 chars). */
  primary: string;
  /** Second line — shown when collapsed (hint). */
  detail: string | null;
  /** Full markdown content for expanded view. */
  expandedContent: string;
}

/** Extract a human-readable preview from a regen proposal item.
 *  Pure function — no I/O, no side effects. */
export function extractRegenPreview(item: unknown, stage: PartialRegenStage): ItemPreview | null {
  if (!item || typeof item !== 'object') return null;
  const it = item as Record<string, unknown>;

  const str = (key: string, fallback?: string): string => {
    const v = it[key];
    return typeof v === 'string' ? v : (fallback ?? '');
  };
  const optStr = (key: string): string | null => {
    const v = it[key];
    return typeof v === 'string' ? v : null;
  };
  const arr = (key: string): unknown[] => {
    const v = it[key];
    return Array.isArray(v) ? v : [];
  };
  const id = (): string => str('id', `item-${Math.random().toString(36).slice(2, 8)}`);

  switch (stage) {
    case 'mcqs': {
      const options = (arr('options') as string[]).map((o, i) => `${String.fromCharCode(65 + i)}) ${o}`).join('\n');
      const correct = typeof it['correct'] === 'number' ? String.fromCharCode(65 + (it['correct'] as number)) : 'N/A';
      const question = str('question') || str('stem');
      return {
        id: id(),
        kindLabel: 'MCQ',
        primary: question.slice(0, 120),
        detail: options.slice(0, 200),
        expandedContent: `**Question:** ${question}\n\n**Options:**\n${options}\n\n**Correct:** ${correct}`,
      };
    }
    case 'true_false': {
      const statement = str('statement');
      const correct = it['correct'] === true ? 'True ✅' : 'False ❌';
      const explanation = optStr('explanation');
      return {
        id: id(),
        kindLabel: 'T/F',
        primary: statement.slice(0, 120),
        detail: `Correct: ${it['correct'] ? 'True' : 'False'}`,
        expandedContent: `**Statement:** ${statement}\n\n**Correct:** ${correct}${explanation ? `\n\n**Explanation:** ${explanation}` : ''}`,
      };
    }
    case 'flashcards': {
      const front = str('front');
      const back = str('back');
      return {
        id: id(),
        kindLabel: 'Flashcard',
        primary: front.slice(0, 120),
        detail: back ? back.slice(0, 200) : null,
        expandedContent: `**Front:** ${front}\n\n**Back:** ${back}`,
      };
    }
    case 'essays': {
      const prompt = str('prompt');
      const modelAnswer = optStr('modelAnswer');
      const keyPoints = arr('keyPoints') as string[];
      return {
        id: id(),
        kindLabel: 'Essay',
        primary: prompt.slice(0, 120),
        detail: modelAnswer ? `Model answer: ${modelAnswer.slice(0, 150)}…` : null,
        expandedContent: `**Prompt:** ${prompt}\n\n**Model Answer:** ${modelAnswer ?? 'N/A'}${keyPoints.length ? `\n\n**Key Points:**\n${keyPoints.map((p) => `- ${p}`).join('\n')}` : ''}`,
      };
    }
    case 'glossary': {
      const term = str('term');
      const definition = str('definition');
      const example = optStr('example');
      return {
        id: id(),
        kindLabel: 'Glossary',
        primary: term.slice(0, 120),
        detail: definition ? definition.slice(0, 200) : null,
        expandedContent: `**Term:** ${term}\n\n**Definition:** ${definition}${example ? `\n\n**Example:** ${example}` : ''}`,
      };
    }
    case 'mindmaps': {
      const label = str('label') || str('topic');
      const topic = optStr('topic');
      const nodes = arr('nodes') as Array<Record<string, unknown>>;
      return {
        id: id(),
        kindLabel: 'Mindmap',
        primary: label.slice(0, 120),
        detail: topic,
        expandedContent: `**Label:** ${label}\n\n**Topic:** ${topic ?? ''}${nodes.length ? `\n\n**Nodes:**\n${nodes.map((n) => `- ${String(n['label'] ?? n['id'] ?? '?')}`).join('\n')}` : ''}`,
      };
    }
    case 'lecture_notes': {
      const title = str('title');
      const summary = optStr('summary');
      const body = str('body');
      return {
        id: id(),
        kindLabel: 'Notes',
        primary: title.slice(0, 120),
        detail: summary ? summary.slice(0, 200) : null,
        expandedContent: `**Title:** ${title}\n\n**Summary:** ${summary ?? ''}\n\n**Body:**\n${body}`,
      };
    }
    case 'proof_drills': {
      const prompt = str('prompt');
      const keySteps = arr('keySteps');
      const stepStrs = keySteps.map((s, i) => {
        if (typeof s === 'string') return `${i + 1}. ${s}`;
        if (typeof s === 'object' && s !== null) {
          return `${i + 1}. ${String((s as Record<string, unknown>)['instruction'] ?? s)}`;
        }
        return `${i + 1}. ?`;
      });
      return {
        id: id(),
        kindLabel: 'Proof',
        primary: prompt.slice(0, 120),
        detail: keySteps.length ? `${keySteps.length} steps` : null,
        expandedContent: `**Prompt:** ${prompt}\n\n**Key Steps:**\n${stepStrs.join('\n')}`,
      };
    }
    case 'free_responses': {
      const prompt = str('prompt');
      const referenceAnswer = optStr('referenceAnswer');
      const reasoning = optStr('reasoning');
      return {
        id: id(),
        kindLabel: 'Free',
        primary: prompt.slice(0, 120),
        detail: referenceAnswer ? `Reference: ${referenceAnswer.slice(0, 150)}…` : null,
        expandedContent: `**Prompt:** ${prompt}\n\n**Reference Answer:** ${referenceAnswer ?? 'N/A'}${reasoning ? `\n\n**Reasoning:** ${reasoning}` : ''}`,
      };
    }
    case 'reading_passages': {
      const title = str('title');
      const passage = str('passage');
      const questionRefs = arr('questionRefs');
      return {
        id: id(),
        kindLabel: 'Reading',
        primary: title.slice(0, 120),
        detail: passage ? `${passage.slice(0, 150)}…` : `${questionRefs.length} questions`,
        expandedContent: `**Title:** ${title}\n\n**Passage:**\n${passage}\n\n**Questions:** ${questionRefs.length} linked`,
      };
    }
    default:
      return {
        id: id(),
        kindLabel: 'Item',
        primary: JSON.stringify(item).slice(0, 120),
        detail: null,
        expandedContent: JSON.stringify(item, null, 2),
      };
  }
}
